import { Plus } from "lucide-react";
import { useRef } from "react";
import type { Cup } from "../types/models";
import { formatVolume } from "../lib/units";
import { usePreferences } from "../app/PreferencesProvider";
import { VesselIcon } from "./VesselIcon";

type CupButtonProps = {
  cup?: Cup;
  label: string;
  onDrink?: (cup: Cup) => void;
  onEdit?: (cup: Cup) => void;
  onCreate?: () => void;
};

export function CupButton({ cup, label, onDrink, onEdit, onCreate }: CupButtonProps) {
  const { preferences } = usePreferences();
  const timer = useRef<number | undefined>(undefined);
  const longPressTriggered = useRef(false);

  const startLongPress = () => {
    if (!cup || !onEdit) {
      return;
    }
    longPressTriggered.current = false;
    timer.current = window.setTimeout(() => {
      longPressTriggered.current = true;
      onEdit(cup);
    }, 550);
  };

  const clearLongPress = () => {
    if (timer.current) {
      window.clearTimeout(timer.current);
      timer.current = undefined;
    }
  };

  if (!cup) {
    return (
      <button className="cup-button cup-button--add" type="button" aria-label={label} onClick={onCreate}>
        <Plus size={22} aria-hidden="true" />
        <span>{label}</span>
      </button>
    );
  }

  return (
    <button
      className="cup-button"
      type="button"
      aria-label={`${cup.name ?? label} ${formatVolume(cup.volume_ml ?? 0, preferences.volumeUnit)}`}
      onPointerDown={startLongPress}
      onPointerUp={clearLongPress}
      onPointerLeave={clearLongPress}
      onContextMenu={(event) => {
        event.preventDefault();
        onEdit?.(cup);
      }}
      onClick={() => {
        if (!longPressTriggered.current) {
          onDrink?.(cup);
        }
        longPressTriggered.current = false;
      }}
    >
      <VesselIcon kind={cup.icon} />
      <strong>{cup.name ?? label}</strong>
      <span>{formatVolume(cup.volume_ml ?? 0, preferences.volumeUnit)}</span>
    </button>
  );
}
